import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useCurrentInternalUser } from '../lib/currentUser';
import Sidebar from '../components/Sidebar';
import { OttoLoading, OttoPinnedHello, OttoAlive } from '../components/otto';
import { fmtDate } from '../lib/dates';
import StarIcon from '../components/StarIcon';

// Pinned page — flat list of every supplier and brief the current
// user has pinned, split into two sections. Pinning is per-user via
// user_pinned_briefs / user_pinned_suppliers join tables, so what
// Robynne sees here is hers alone. Pinning happens from each item's
// detail-page header (single click toggle on a Pin button). This
// page is the destination for the dashboard panels' "Edit" links
// and for the sidebar Pinned nav item.

// Pinned indicator on list rows — shared StarIcon (filled green).
// Every star on this page is filled by definition (the page only
// shows already-pinned items per the pin-icon-sweep state rule).
// Previously this rendered a 📌 emoji which appeared pink on some
// devices and green on others (emoji are OS-font-rendered, not
// styleable). Now consistent across browsers.
const PinStar = () => (
  <span className="flex-shrink-0 inline-flex items-center"><StarIcon filled size={16} color="#009159" /></span>
);

export default function Pinned() {
  const navigate = useNavigate();
  const { user: currentInternalUser } = useCurrentInternalUser();
  const [suppliers, setSuppliers] = useState([]);
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  // Drag-and-drop state — one pair of refs per list since briefs and
  // suppliers reorder independently. `kind` distinguishes the two so a
  // drag started in one list can't accidentally drop into the other.
  const [dragging, setDragging] = useState(null); // { kind, id } | null
  const [dropTarget, setDropTarget] = useState(null); // { kind, id } | null

  // Persist a new ordering by writing sort_order = index for each row.
  // Done in parallel because the rows are independent. Errors are
  // swallowed; the local state has already taken the new order, so a
  // failed write just means the next page load re-reads whatever the
  // DB has (which would visibly snap back, which we want — surfacing
  // the inconsistency rather than hiding it).
  const persistOrder = async (kind, list) => {
    if (!currentInternalUser?.id) return;
    const table = kind === 'brief' ? 'user_pinned_briefs' : 'user_pinned_suppliers';
    const idCol = kind === 'brief' ? 'brief_id' : 'supplier_id';
    await Promise.all(list.map((item, idx) =>
      supabase
        .from(table)
        .update({ sort_order: idx + 1 })
        .eq('user_id', currentInternalUser.id)
        .eq(idCol, item.id)
    ));
  };

  // Drop = source removed and inserted before target. If target is
  // null (dropped on empty space at end of list), append.
  const handleDrop = (kind, targetId) => {
    if (!dragging || dragging.kind !== kind) return;
    const list = kind === 'brief' ? briefs : suppliers;
    const setList = kind === 'brief' ? setBriefs : setSuppliers;
    const sourceIdx = list.findIndex(x => x.id === dragging.id);
    if (sourceIdx < 0) return;
    const next = [...list];
    const [moved] = next.splice(sourceIdx, 1);
    const targetIdx = targetId ? next.findIndex(x => x.id === targetId) : next.length;
    next.splice(targetIdx < 0 ? next.length : targetIdx, 0, moved);
    setList(next);
    setDragging(null);
    setDropTarget(null);
    persistOrder(kind, next);
  };

  useEffect(() => {
    if (!currentInternalUser?.id) return;
    let cancelled = false;
    (async () => {
      // Two-step join: load the pin rows first, then resolve briefs +
      // suppliers by id. The earlier PostgREST embedded-select approach
      // (briefs(...)/suppliers(...)) silently returned null nested
      // objects whenever schema introspection hadn't refreshed after a
      // migration, dropping the entire list via .filter(Boolean). This
      // is verbose but immune to that whole class of issue.
      // select('*') for the pin rows — resilient to schema variation
      // (e.g. sort_order column not yet added by the drag-reorder
      // migration in some environments). Order by pinned_at desc as
      // a baseline; if sort_order is present, the secondary sort below
      // promotes it. The earlier explicit column list silently
      // errored out when one of those columns was missing.
      const [pinnedSupplierRes, pinnedBriefRes] = await Promise.all([
        supabase
          .from('user_pinned_suppliers')
          .select('*')
          .eq('user_id', currentInternalUser.id)
          .order('pinned_at', { ascending: false }),
        supabase
          .from('user_pinned_briefs')
          .select('*')
          .eq('user_id', currentInternalUser.id)
          .order('pinned_at', { ascending: false }),
      ]);
      if (pinnedSupplierRes.error) {
        // eslint-disable-next-line no-console
        console.error('Pinned suppliers load failed', pinnedSupplierRes.error);
      }
      if (pinnedBriefRes.error) {
        // eslint-disable-next-line no-console
        console.error('Pinned briefs load failed', pinnedBriefRes.error);
      }
      // Client-side secondary sort: when sort_order is populated, use
      // it to override the pinned_at order (matches the drag-reorder
      // UX). When the column is absent, sort_order is undefined for
      // every row → comparison is a no-op and pinned_at ordering wins.
      const bySortOrder = (a, b) => {
        const aS = a.sort_order ?? Number.POSITIVE_INFINITY;
        const bS = b.sort_order ?? Number.POSITIVE_INFINITY;
        if (aS !== bS) return aS - bS;
        return new Date(b.pinned_at || 0) - new Date(a.pinned_at || 0);
      };
      const supplierIds = (pinnedSupplierRes.data || []).slice().sort(bySortOrder).map(r => r.supplier_id);
      const briefIds    = (pinnedBriefRes.data    || []).slice().sort(bySortOrder).map(r => r.brief_id);

      const [suppliersRes, briefsRes] = await Promise.all([
        supplierIds.length > 0
          ? supabase
              .from('suppliers')
              .select('id, supplier_name, category, sub_category, region, preferred_supplier, on_hold, do_not_use_status')
              .in('id', supplierIds)
          : Promise.resolve({ data: [] }),
        briefIds.length > 0
          ? supabase
              .from('briefs')
              .select('id, brief_name, fusion_project_name, brief_category, status, archived_at, required_response_time')
              .in('id', briefIds)
          : Promise.resolve({ data: [] }),
      ]);
      const supplierMap = new Map((suppliersRes.data || []).map(s => [s.id, s]));
      const briefMap    = new Map((briefsRes.data    || []).map(b => [b.id, b]));

      if (!cancelled) {
        // Preserve the pin-row order (sort_order asc, pinned_at desc)
        // by mapping through the original pin lists rather than the
        // resolved tables.
        setSuppliers(supplierIds.map(id => supplierMap.get(id)).filter(Boolean));
        setBriefs(briefIds.map(id => briefMap.get(id)).filter(Boolean));
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentInternalUser?.id]);

  // Inline unpin — deletes the row from user_pinned_* and drops it
  // from the local list. Optimistic: the card vanishes immediately
  // and is restored if the DB delete fails. No confirmation prompt
  // because re-pinning is one click on the item's detail page.
  const unpinSupplier = async (id) => {
    if (!currentInternalUser?.id) return;
    const removed = suppliers.find(s => s.id === id);
    setSuppliers(prev => prev.filter(s => s.id !== id));
    const { error } = await supabase
      .from('user_pinned_suppliers')
      .delete()
      .eq('user_id', currentInternalUser.id)
      .eq('supplier_id', id);
    if (error && removed) setSuppliers(prev => [...prev, removed].sort((a, b) => a.supplier_name.localeCompare(b.supplier_name)));
  };
  const unpinBrief = async (id) => {
    if (!currentInternalUser?.id) return;
    const removed = briefs.find(b => b.id === id);
    setBriefs(prev => prev.filter(b => b.id !== id));
    const { error } = await supabase
      .from('user_pinned_briefs')
      .delete()
      .eq('user_id', currentInternalUser.id)
      .eq('brief_id', id);
    if (error && removed) setBriefs(prev => [...prev, removed].sort((a, b) => (a.brief_name || '').localeCompare(b.brief_name || '')));
  };

  // Response-deadline urgency for pinned brief cards. Mirrors the logic
  // on the Briefs list (src/pages/Briefs.js) — red when overdue, amber
  // when due within seven days, neutral otherwise. Only live briefs
  // (status='sent', not archived) carry urgency. Returns null when no
  // deadline is set so the row collapses instead of rendering an em-dash.
  const deadlineMeta = (brief) => {
    if (!brief.required_response_time) return null;
    const todayMid = new Date(); todayMid.setHours(0, 0, 0, 0);
    const sevenDaysOut = new Date(todayMid); sevenDaysOut.setDate(sevenDaysOut.getDate() + 7);
    const d = new Date(brief.required_response_time);
    const isLive = brief.status === 'sent' && !brief.archived_at;
    const isOverdue = isLive && d < todayMid;
    const isDueThisWeek = isLive && d >= todayMid && d <= sevenDaysOut;
    // Overdue is the single red on the page; every other due date reads
    // in neutral ink (build #11).
    const tone = isOverdue ? 'text-[#E0290A]' : 'text-[#4C4E42]';
    const suffix = isOverdue ? ' · Overdue'
      : isDueThisWeek ? ' · Due this week'
      : '';
    return { label: fmtDate(d), tone, suffix };
  };

  // Small × icon used as the unpin affordance on each card. Sits in
  // the top-right corner; clicking it bypasses the card's nav button
  // because the two are siblings, not nested.
  const UnpinIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );

  // Otto's list-aware "hello" greeting + the empty-Pinned teaching state.
  const firstName = (currentInternalUser?.full_name || '').trim().split(/\s+/)[0] || '';
  const overduePins = briefs.filter(b => { const m = deadlineMeta(b); return m && m.suffix === ' · Overdue'; }).length;
  const isEmptyPinned = !loading && suppliers.length === 0 && briefs.length === 0;
  const pinnedGreeting = overduePins > 0
    ? `${overduePins} of your pins ${overduePins === 1 ? 'is' : 'are'} running overdue — worth a look.`
    : (firstName ? `Hey ${firstName} 👋 Your shortlist's looking good.` : "Hey 👋 Your shortlist's looking good.");

  return (
    <div className="min-h-screen bg-canvas flex">

      <Sidebar currentInternalUser={currentInternalUser} />

      <div className="relative flex-1 flex flex-col">
        {/* Otto's once-per-session hello fly-through — non-blocking overlay,
            pointer-events off. Mounted whenever the page isn't the empty-
            Pinned teaching state; `ready` holds the fly-through until loading
            finishes so he doesn't fly in over the spinner (and doesn't burn
            the session's single showing while still loading). */}
        {!isEmptyPinned && <OttoPinnedHello greeting={pinnedGreeting} ready={!loading} />}
        <div className="pt-8">
          <div className="px-8 flex items-start justify-between gap-6 mb-6">
            <div className="min-w-0">
              <h1 className="fusion-h1 text-ink-primary">Pinned</h1>
              <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mt-2">Quick access to the suppliers and briefs you've flagged for follow-up.</p>
            </div>
            <p className="font-['Metropolis'] text-xs text-[#51534A] tracking-[1px] pt-2 flex-shrink-0">
              {loading ? '' : `${suppliers.length + briefs.length} pinned`}
            </p>
          </div>
          <div className="mx-8 h-px bg-[#E6E9DA]" aria-hidden="true" />
        </div>

        <div className="flex-1 p-8 space-y-10">
          {loading ? (
            <div className="py-10"><OttoLoading size="medium" message="Loading pinned items" /></div>
          ) : isEmptyPinned ? (
            // Empty Pinned is the one place Otto stays (Everyday, gentle idle
            // bob) as a teaching empty state rather than flying off.
            <div className="flex flex-col items-center justify-center text-center py-16 gap-4">
              <OttoAlive size={120} />
              <div>
                <p className="font-['Metropolis'] text-[16px] font-bold text-ink-primary">Nothing pinned yet</p>
                <p className="font-['Metropolis'] text-[13px] text-ink-2 mt-1.5 max-w-[42ch] leading-relaxed">
                  Star a supplier or brief to keep it here for quick follow-up.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Pinned Suppliers section */}
              <section>
                <div className="flex items-baseline gap-2 mb-4">
                  <h2 className="font-['Metropolis'] text-[11px] font-bold tracking-[0.12em] uppercase text-fusion-emerald">Pinned Suppliers</h2>
                  <span className="font-['Metropolis'] text-[11px] tracking-[0.12em] uppercase text-ink-3">· {suppliers.length}</span>
                </div>
                {suppliers.length === 0 ? (
                  <div className="bg-white border border-line rounded-xl p-6">
                    <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mb-1">No suppliers pinned yet.</p>
                    <p className="font-['Metropolis'] text-xs text-[#51534A]">
                      Open any supplier and click <span className="text-fusion-forest font-semibold">Pin</span> in the header to add them here.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
                    {suppliers.map(s => {
                      const isDragging = dragging?.kind === 'supplier' && dragging.id === s.id;
                      const isDropTarget = dropTarget?.kind === 'supplier' && dropTarget.id === s.id && !isDragging;
                      return (
                        <div
                          key={s.id}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.effectAllowed = 'move';
                            // setData is required for Firefox to start the drag.
                            e.dataTransfer.setData('text/plain', s.id);
                            setDragging({ kind: 'supplier', id: s.id });
                          }}
                          onDragOver={(e) => {
                            if (dragging?.kind !== 'supplier') return;
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                            if (!isDropTarget) setDropTarget({ kind: 'supplier', id: s.id });
                          }}
                          onDragLeave={(e) => {
                            // Only clear the marker when the cursor truly leaves
                            // the card (not when it enters a child element). The
                            // dragLeave fires on every child boundary otherwise.
                            if (e.currentTarget === e.target) setDropTarget(null);
                          }}
                          onDrop={(e) => { e.preventDefault(); handleDrop('supplier', s.id); }}
                          onDragEnd={() => { setDragging(null); setDropTarget(null); }}
                          className={`relative bg-white border rounded-[10px] shadow-[0_1px_2px_rgba(19,19,19,0.03)] transition-colors group cursor-move ${
                            isDragging ? 'opacity-40 border-[#92D400]/40' :
                            isDropTarget ? 'border-[#92D400] border-t-[3px]' :
                            'border-hair-admin hover:bg-table-zebra'
                          }`}>
                          {/* Two lines (Pinned FIX §2): star + name, then a
                              category · region meta line — so a pinned supplier
                              carries comparable weight to the pinned-brief
                              cards and isn't a thin, navigation-only row.
                              Reverses the #16 name-only density for parity. */}
                          <button
                            onClick={() => navigate(`/suppliers/${s.id}`)}
                            className="w-full text-left px-3.5 py-3 pr-9">
                            <div className="flex items-center gap-2">
                              <PinStar />
                              <p className="font-['Metropolis'] text-[13.5px] font-bold text-ink truncate group-hover:text-active-green transition-colors" title={s.supplier_name}>
                                {s.supplier_name}
                              </p>
                            </div>
                            <p className="font-['Metropolis'] text-xs text-ink-3 pl-6 mt-1 truncate">
                              {[s.category, s.sub_category, s.region].filter(Boolean).join(' · ') || 'Supplier on file'}
                            </p>
                          </button>
                          <button
                            onClick={() => unpinSupplier(s.id)}
                            title="Unpin supplier"
                            aria-label={`Unpin ${s.supplier_name}`}
                            className="absolute top-2 right-2 text-[#51534A] hover:text-[#E0290A] transition-all p-1 opacity-0 group-hover:opacity-100 focus:opacity-100">
                            <UnpinIcon />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* Pinned Briefs section */}
              <section>
                <div className="flex items-baseline gap-2 mb-4">
                  <h2 className="font-['Metropolis'] text-[11px] font-bold tracking-[0.12em] uppercase text-fusion-emerald">Pinned Briefs</h2>
                  <span className="font-['Metropolis'] text-[11px] tracking-[0.12em] uppercase text-ink-3">· {briefs.length}</span>
                </div>
                {briefs.length === 0 ? (
                  <div className="bg-white border border-line rounded-xl p-6">
                    <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mb-1">No briefs pinned yet.</p>
                    <p className="font-['Metropolis'] text-xs text-[#51534A]">
                      Open any brief and click <span className="text-fusion-forest font-semibold">Pin</span> in the header to add it here.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
                    {briefs.map(b => {
                      const dm = deadlineMeta(b);
                      const isDragging = dragging?.kind === 'brief' && dragging.id === b.id;
                      const isDropTarget = dropTarget?.kind === 'brief' && dropTarget.id === b.id && !isDragging;
                      return (
                        <div
                          key={b.id}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', b.id);
                            setDragging({ kind: 'brief', id: b.id });
                          }}
                          onDragOver={(e) => {
                            if (dragging?.kind !== 'brief') return;
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                            if (!isDropTarget) setDropTarget({ kind: 'brief', id: b.id });
                          }}
                          onDragLeave={(e) => {
                            if (e.currentTarget === e.target) setDropTarget(null);
                          }}
                          onDrop={(e) => { e.preventDefault(); handleDrop('brief', b.id); }}
                          onDragEnd={() => { setDragging(null); setDropTarget(null); }}
                          className={`relative bg-white border rounded-[10px] shadow-[0_1px_2px_rgba(19,19,19,0.03)] transition-colors group cursor-move ${
                            isDragging ? 'opacity-40 border-[#92D400]/40' :
                            isDropTarget ? 'border-[#92D400] border-t-[3px]' :
                            'border-hair-admin hover:bg-table-zebra'
                          }`}>
                          {/* Three tidy lines (build #16): title / status-dot +
                              project · status / due. Status dot is green for a
                              live (sent) brief, neutral grey otherwise. Titles
                              truncate rather than wrapping to a fourth line. */}
                          <button
                            onClick={() => navigate(`/briefs/${b.id}`)}
                            className="w-full text-left px-3.5 py-3 pr-9">
                            <div className="flex items-center gap-2">
                              <PinStar />
                              <p className="font-['Metropolis'] text-[13.5px] font-bold text-ink truncate group-hover:text-active-green transition-colors" title={b.brief_name}>
                                {b.brief_name || 'Untitled brief'}
                              </p>
                            </div>
                            <p className="font-['Metropolis'] text-xs text-ink-3 pl-6 mt-1 truncate flex items-center gap-1.5">
                              <span
                                aria-hidden="true"
                                className="w-[5px] h-[5px] rounded-full flex-shrink-0"
                                style={{ background: b.status === 'sent' ? '#92D400' : '#b7baa9' }} />
                              <span className="truncate">
                                {[b.fusion_project_name, b.status].filter(Boolean).join(' · ') || '—'}
                              </span>
                            </p>
                            {dm && (
                              <p className={`font-['Metropolis'] text-xs font-semibold pl-6 mt-1 truncate ${dm.tone}`}>
                                Due {dm.label}{dm.suffix}
                              </p>
                            )}
                          </button>
                          <button
                            onClick={() => unpinBrief(b.id)}
                            title="Unpin brief"
                            aria-label={`Unpin ${b.brief_name || 'brief'}`}
                            className="absolute top-2 right-2 text-[#51534A] hover:text-[#E0290A] transition-all p-1 opacity-0 group-hover:opacity-100 focus:opacity-100">
                            <UnpinIcon />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
